import { useState } from "react";
import { gql, useQuery } from "@apollo/client";

interface MenuCategory {
	id: string;
	name: string;
	startHour: string | null;
	endHour: string | null;
	order: number;
	isVisible: boolean;
	showInMenu: boolean;
	imageUrl: string;
	items: {
		id: string;
		isVisible: boolean;
		order: number;
		showInMenu: boolean;
		product: {
			id: string;
			name: string;
			displayName: string;
			description: string | null;
			price: number;
		};
	}[];
	groups: {
		id: string;
		name: string;
		order: number;
		showAs: "list" | "inline" | "none";
		showInMenu: boolean;
		items: {
			id: string;
			isVisible: boolean;
			order: number;
			showInMenu: boolean;
			product: {
				id: string;
				name: string;
				displayName: string;
				description: string | null;
				price: number;
			};
		}[];
	}[];
}

const QUERY = gql`
	{
		response: menuCategories {
			id
			name: displayName
			startHour
			endHour
			order: displayOrder
			isVisible
			showInMenu
			imageUrl
			items {
				id
				isVisible
				showInMenu
				order: displayOrder
				product {
					id
					name
					displayName
					description
					price
				}
			}
			groups {
				id
				name: displayName
				order: displayOrder
				showAs
				showInMenu
				items {
					id
					isVisible
					showInMenu
					order: displayOrder
					product {
						id
						name
						displayName
						description
						price
					}
				}
			}
		}
	}
`;

function transformCategory(category: MenuCategory) {
	const { items, groups, ...rest } = category;

	const joinedItems = [
		...items.map((item) => ({ ...item })),
		...groups.map((group) => ({ ...group })),
	];
	joinedItems.sort((a, b) => a.order - b.order);

	return { ...rest, items: joinedItems };
}

export function useGetMenuCategories() {
	const [categories, setCategories] = useState<
		ReturnType<typeof transformCategory>[]
	>([]);
	const { loading, error } = useQuery<{ response: MenuCategory[] }>(QUERY, {
		onCompleted: (data) => {
			const transformedCategories = data.response.map(transformCategory);
			setCategories(transformedCategories);
		},
		fetchPolicy: "cache-and-network",
	});

	return {
		categories,
		loading,
		error,
	};
}
